import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, blobUrlAuthed, downloadAuthed, t } from "../lib/api";
import { fmtClock, fmtDate, fmtDuration, useAsync, usePoll } from "../lib/hooks";
import { useSession } from "../lib/session";
import { Empty, ErrorNote, SeverityPill, StatusPill } from "../components/ui";

interface Evidence {
  turnSeq: number;
  quote: string;
}
interface Finding {
  id: string;
  idx: number;
  kind: string;
  title: string;
  detail: string;
  category: string | null;
  severity: string | null;
  addressesIdx: number | null;
  evidence: Evidence[];
  reviewStatus: string;
}
interface Step {
  id: string;
  order: number;
  lane: string;
  name: string;
  description: string;
  issues: string[];
  evidence: Evidence[];
  reviewStatus: string;
}
interface Detail {
  id: string;
  engagementId: string;
  engagementName: string;
  status: string;
  statusNote: string | null;
  liveState: string;
  interviewerLang: string;
  intervieweeLang: string;
  startedAt: string | null;
  recordedSeconds: number;
  consent: { accepted: boolean; at: string; lang: string; textVersion: string } | null;
  briefing: { headline: string; knownSoFar: string[]; probeFor: string[]; claimsToVerify: string[]; suggestedQuestions: string[] } | null;
  reportLanguage: string;
  interviewee: { pseudonym: string; displayName: string | null; profile: Record<string, unknown>; language: string };
  focusModules: Array<{ key: string; name: string; disclaimer: string; checklist: Array<{ key: string; area: string; check: string }> }>;
  analysis: { status: string; summary: string | null; interviewee_role: string | null; open_questions: string[]; model: string; error: string | null; version: number } | null;
  steps: Step[];
  findings: Finding[];
  activities: Array<{ id: string; activity: string; role: string; timePerOccurrence: Range | null; frequency: { count: number | null; per: string } | null; volume: string | null; waitTime: Range | null; confidence: string }>;
  moduleChecks: Array<{ moduleKey: string; itemKey: string; status: string; note: string; evidence: Evidence[] }>;
  recordings: Array<{ track: string; durationMs: number }>;
  reports: Array<{ id: string; lang: string; status: string; error: string | null; createdAt: string }>;
  latency: { median: number | null; p90: number | null; n: number };
}
type Range = { min: number | null; max: number | null; unit: string };
interface Turn {
  id: string;
  seq: number;
  speaker: string;
  startedMs: number;
  sourceLang: string;
  targetLang: string;
  sourceText: string;
  targetText: string | null;
  origin: string;
  edited: boolean;
  preparedQuestionId: string | null;
  speakerLabel?: string | null;
}

const KIND_LABEL: Record<string, string> = { takeaway: "Key takeaways", gap: "Process gaps", risk: "Risks and compliance", suggestion: "Suggestions", quick_win: "Quick wins" };
const KIND_ORDER = ["takeaway", "gap", "risk", "suggestion", "quick_win"];
const CHECK: Record<string, string> = { ok: "In place", gap: "Gap", partial: "Partial", unknown: "Not discussed" };

function range(r: Range | null) {
  if (!r || (r.min == null && r.max == null)) return "—";
  return r.min != null && r.max != null && r.min !== r.max ? `${r.min}–${r.max} ${r.unit}` : `${r.min ?? r.max} ${r.unit}`;
}

export function InterviewPage() {
  const { tenant, id } = useParams();
  const { config } = useSession();
  const base = `${t(tenant!)}/interviews/${id}`;
  const d = useAsync(() => api.get<Detail>(base), [base]);
  const turns = useAsync(() => api.get<Turn[]>(`${base}/transcript`), [base]);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [flash, setFlash] = useState<number | null>(null);
  const [reportLang, setReportLang] = useState<string>("");
  const audio = useRef<HTMLAudioElement>(null);
  const processing = d.data && ["processing", "live"].includes(d.data.status);
  const pendingReport = d.data?.reports.some((r) => r.status === "queued" || r.status === "running");
  usePoll(() => {
    void d.reload();
    if (processing) void turns.reload();
  }, 4000, Boolean(processing || pendingReport));

  useEffect(() => {
    if (d.data && !reportLang) setReportLang(d.data.reportLanguage);
  }, [d.data, reportLang]);
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  if (d.error) return <ErrorNote error={d.error} />;
  if (!d.data) return <p className="muted">Loading…</p>;
  const x = d.data;
  const locked = x.status === "verified";
  const canReview = ["draft", "reviewed"].includes(x.status);
  const pending = x.findings.filter((f) => f.reviewStatus === "pending").length + x.steps.filter((s) => s.reviewStatus === "pending").length;

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await d.reload();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  };

  const seek = async (seq: number) => {
    const turn = turns.data?.find((tt) => tt.seq === seq);
    setFlash(seq);
    document.getElementById(`turn-${seq}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (!turn || !x.recordings.length) return;
    let url = audioUrl;
    if (!url) {
      url = await blobUrlAuthed(`${base}/audio/mixed`).catch(() => null);
      if (!url) return;
      setAudioUrl(url);
    }
    setTimeout(() => {
      if (audio.current) {
        audio.current.currentTime = Math.max(0, turn.startedMs / 1000 - 0.3);
        void audio.current.play();
      }
    }, 50);
  };

  const Ev = ({ ev }: { ev: Evidence[] }) =>
    ev.length ? (
      <div className="ev">
        {ev.slice(0, 3).map((e, i) => {
          const tt = turns.data?.find((y) => y.seq === e.turnSeq);
          return (
            <div key={i}>
              <button onClick={() => void seek(e.turnSeq)} title="Play this moment">
                {tt ? fmtClock(tt.startedMs) : `#${e.turnSeq}`}
              </button>{" "}
              “{e.quote}”
            </div>
          );
        })}
      </div>
    ) : null;

  const review = (kind: "findings" | "steps", itemId: string, status: string, extra: Record<string, unknown> = {}) =>
    act(`${itemId}`, () => api.patch(`${base}/${kind}/${itemId}`, { status, ...extra }));

  const stepIdx = x.status === "verified" ? 3 : x.status === "reviewed" ? 2 : x.status === "draft" ? 1 : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="muted small">
            <Link to={`/w/${tenant}/e/${x.engagementId}/interviews`}>{x.engagementName}</Link>
          </p>
          <h1>
            {x.interviewee.pseudonym}
            {x.interviewee.displayName && <span className="muted" style={{ fontWeight: 400, fontSize: 18 }}> · {x.interviewee.displayName}</span>}
          </h1>
          <p className="sub">
            {[x.analysis?.interviewee_role ?? x.interviewee.profile.designation, x.interviewee.profile.department, x.interviewee.profile.location].filter(Boolean).join(" · ")}
            {x.startedAt && ` · ${fmtDate(x.startedAt)} · ${fmtDuration(x.recordedSeconds)}`}
          </p>
        </div>
        <span className="spacer" />
        <StatusPill status={x.status} />
      </div>

      {["planned", "live"].includes(x.status) && <Prepare detail={x} onChange={() => void d.reload()} />}

      {x.status === "processing" && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <h3>Putting the interview together</h3>
          <p className="muted" style={{ marginTop: 6 }}>Assembling the recordings, removing personal details and analysing the conversation. This usually takes a minute or two.</p>
        </div>
      )}
      {x.status === "failed" && (
        <div className="error-box" style={{ marginBottom: 20 }}>
          The analysis didn't complete{x.analysis?.error ? `: ${x.analysis.error}` : ""}.{" "}
          <button className="btn small" onClick={() => void act("re", () => api.post(`${base}/reanalyze`))}>
            Run the analysis again
          </button>
        </div>
      )}

      {["draft", "reviewed", "verified"].includes(x.status) && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <div className="row wrap" style={{ gap: 16 }}>
            <div className="stepper" aria-label="Review progress">
              {["Recorded", "Draft", "Reviewed", "Verified"].map((label, i) => (
                <span key={label} style={{ display: "contents" }}>
                  {i > 0 && <span className="bar" />}
                  <span className={`s ${i < stepIdx || (i === 3 && stepIdx === 3) ? "done" : i === stepIdx ? "now" : ""}`}>
                    <b>{i < stepIdx || stepIdx === 3 ? "✓" : i + 1}</b>
                    {label}
                  </span>
                </span>
              ))}
            </div>
            <span className="spacer" />
            {canReview && pending > 0 && (
              <>
                <span className="muted small">{pending} {pending === 1 ? "item" : "items"} still to review</span>
                <button className="btn small" disabled={!!busy} onClick={() => void act("all", () => api.post(`${base}/accept-remaining`))}>
                  Accept the rest
                </button>
              </>
            )}
            {x.status === "draft" && (
              <button className="btn" disabled={!!busy} onClick={() => void act("rev", () => api.post(`${base}/transition`, { to: "reviewed" }))}>
                Mark reviewed
              </button>
            )}
            {canReview && (
              <button className="btn primary" disabled={!!busy || pending > 0} title={pending ? "Review every item first" : ""} onClick={() => void act("ver", () => api.post(`${base}/transition`, { to: "verified" }))}>
                Verify interview
              </button>
            )}
            {locked && (
              <button className="btn" disabled={!!busy} onClick={() => void act("draft", () => api.post(`${base}/transition`, { to: "draft" }))}>
                Return to draft
              </button>
            )}
          </div>
          <div className="row wrap" style={{ marginTop: 14, gap: 10 }}>
            <select className="input" style={{ width: 200 }} value={reportLang} onChange={(e) => setReportLang(e.target.value)} aria-label="Report language">
              {config.languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
            <button className="btn" disabled={!!busy} onClick={() => void act("pdf", () => api.post(`${base}/reports`, { lang: reportLang }))}>
              Create PDF report
            </button>
            {x.reports.slice(0, 4).map((r) => (
              <span key={r.id} className="row small" style={{ gap: 6 }}>
                {r.status === "done" ? (
                  <button className="btn small ghost" onClick={() => void downloadAuthed(`${t(tenant!)}/reports/${r.id}/download`, `${x.interviewee.pseudonym}-${r.lang}.pdf`)}>
                    Download {r.lang} · {fmtDate(r.createdAt)}
                  </button>
                ) : r.status === "failed" ? (
                  <span className="pill red" title={r.error ?? ""}>{r.lang} report failed</span>
                ) : (
                  <span className="pill blue">{r.lang} report in progress</span>
                )}
              </span>
            ))}
            <span className="spacer" />
            {canReview && (
              <button className="btn small ghost" disabled={!!busy} onClick={() => void act("re", () => api.post(`${base}/reanalyze`))}>
                Run the analysis again
              </button>
            )}
          </div>
          <ErrorNote error={error} />
        </div>
      )}

      {x.recordings.length > 0 && (
        <div className="row" style={{ marginBottom: 12 }}>
          {audioUrl ? (
            <audio ref={audio} src={audioUrl} controls style={{ width: "100%" }} />
          ) : (
            <button className="btn small" onClick={async () => setAudioUrl(await blobUrlAuthed(`${base}/audio/mixed`))}>
              Load the recording
            </button>
          )}
        </div>
      )}

      {(x.analysis || (turns.data?.length ?? 0) > 0) && (
        <div className="review">
          <section className="panel flush">
            <div className="panel-head" style={{ padding: "14px 16px 0" }}>
              <h3>Transcript</h3>
              <p>{x.interviewerLang} ↔ {x.intervieweeLang}{x.latency.median != null ? ` · median delay ${(x.latency.median / 1000).toFixed(1)} s` : ""}</p>
            </div>
            <div className="turns">
              {(turns.data ?? []).map((tt) => (
                <TurnRow key={tt.id} turn={tt} base={base} flash={flash === tt.seq} editable={!locked && canReview} onSaved={() => void turns.reload()} onPlay={() => void seek(tt.seq)} />
              ))}
              {turns.data?.length === 0 && <Empty title="No speech recorded" />}
            </div>
          </section>

          <div className="stack">
            {x.analysis?.summary && (
              <section className="panel">
                <div className="panel-head">
                  <h3>Summary</h3>
                  <p>{x.analysis.model} · version {x.analysis.version}</p>
                </div>
                <p>{x.analysis.summary}</p>
              </section>
            )}

            {x.steps.length > 0 && (
              <section className="panel">
                <div className="panel-head">
                  <h3>Process as described</h3>
                </div>
                <div className="stack tight">
                  {x.steps.map((s) => (
                    <Item
                      key={s.id}
                      kind="takeaway"
                      title={`${s.order}. ${s.name}`}
                      meta={<span className="muted xs">{s.lane}{s.issues.length ? ` · ${s.issues.join(", ")}` : ""}</span>}
                      detail={s.description}
                      ev={<Ev ev={s.evidence} />}
                      status={s.reviewStatus}
                      locked={locked || !canReview}
                      onReview={(st, edit) => review("steps", s.id, st, edit ? { name: edit.title, description: edit.detail } : {})}
                    />
                  ))}
                </div>
              </section>
            )}

            {KIND_ORDER.map((k) => {
              const items = x.findings.filter((f) => f.kind === k);
              if (!items.length) return null;
              return (
                <section className="panel" key={k}>
                  <div className="panel-head">
                    <h3>{KIND_LABEL[k]}</h3>
                  </div>
                  <div className="stack tight">
                    {items.map((f) => (
                      <Item
                        key={f.id}
                        kind={f.kind}
                        title={f.title}
                        meta={
                          <span className="row" style={{ gap: 6 }}>
                            <SeverityPill severity={f.severity} />
                            {f.category && <span className="pill">{f.category.replace(/_/g, " ")}</span>}
                            {f.addressesIdx != null && <span className="muted xs">Addresses: {x.findings.find((g) => g.idx === f.addressesIdx)?.title}</span>}
                          </span>
                        }
                        detail={f.detail}
                        ev={<Ev ev={f.evidence} />}
                        status={f.reviewStatus}
                        locked={locked || !canReview}
                        onReview={(st, edit) => review("findings", f.id, st, edit ? { title: edit.title, detail: edit.detail } : {})}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            {x.activities.length > 0 && (
              <section className="panel">
                <div className="panel-head">
                  <h3>Time and effort</h3>
                  <p>The interviewee's own estimates</p>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Each time</th>
                      <th>How often</th>
                      <th>Waiting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {x.activities.map((a) => (
                      <tr key={a.id}>
                        <td>
                          {a.activity}
                          <div className="muted xs">{a.role} · {a.confidence} confidence{a.volume ? ` · ${a.volume}` : ""}</div>
                        </td>
                        <td>{range(a.timePerOccurrence)}</td>
                        <td>{a.frequency ? `${a.frequency.count ?? "?"} per ${a.frequency.per}` : "—"}</td>
                        <td>{range(a.waitTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {x.focusModules.map((m) => (
              <section className="panel" key={m.key}>
                <div className="panel-head">
                  <h3>{m.name}</h3>
                </div>
                <table className="table checkmatrix">
                  <tbody>
                    {m.checklist.map((c) => {
                      const r = x.moduleChecks.find((y) => y.moduleKey === m.key && y.itemKey === c.key);
                      const st = r?.status ?? "unknown";
                      return (
                        <tr key={c.key}>
                          <td style={{ width: "34%" }}>
                            <b>{c.area}</b>
                          </td>
                          <td className={`c-${st}`} style={{ width: 110 }}>{CHECK[st]}</td>
                          <td>
                            {r?.note ?? "Not discussed."}
                            {r && <Ev ev={r.evidence} />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="muted xs" style={{ marginTop: 8 }}>{m.disclaimer}</p>
              </section>
            ))}

            {!!x.analysis?.open_questions?.length && (
              <section className="panel">
                <div className="panel-head">
                  <h3>Open questions</h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {x.analysis.open_questions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </section>
            )}
            {x.consent && (
              <p className="muted xs">
                Consent given on the participant's phone, {new Date(x.consent.at).toLocaleString()} ({x.consent.lang}, text version {x.consent.textVersion}).
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Item(props: {
  kind: string;
  title: string;
  meta?: React.ReactNode;
  detail: string;
  ev: React.ReactNode;
  status: string;
  locked: boolean;
  onReview: (status: string, edit?: { title: string; detail: string }) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(props.title);
  const [detail, setDetail] = useState(props.detail);
  return (
    <div className={`item kind-${props.kind} ${props.status === "rejected" ? "rejected" : ""}`}>
      {editing ? (
        <div className="stack tight">
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea className="input" value={detail} onChange={(e) => setDetail(e.target.value)} />
          <div className="row">
            <button className="btn small primary" onClick={async () => {
              await props.onReview("edited", { title: title.replace(/^\d+\.\s*/, ""), detail });
              setEditing(false);
            }}>
              Save edit
            </button>
            <button className="btn small ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="row" style={{ alignItems: "flex-start" }}>
            <b style={{ flex: 1 }}>{props.title}</b>
            {props.status !== "pending" && <span className={`pill ${props.status === "rejected" ? "red" : "green"}`}>{props.status === "edited" ? "Edited" : props.status === "accepted" ? "Accepted" : "Rejected"}</span>}
          </div>
          {props.meta && <div style={{ marginTop: 6 }}>{props.meta}</div>}
          <p style={{ marginTop: 6 }}>{props.detail}</p>
          {props.ev}
          {!props.locked && (
            <div className="actions">
              <button className="btn small" onClick={() => void props.onReview("accepted")}>Accept</button>
              <button className="btn small" onClick={() => setEditing(true)}>Edit</button>
              <button className="btn small danger" onClick={() => void props.onReview("rejected")}>Reject</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TurnRow({ turn, base, flash, editable, onSaved, onPlay }: { turn: Turn; base: string; flash: boolean; editable: boolean; onSaved: () => void; onPlay: () => void }) {
  const [editing, setEditing] = useState(false);
  const [src, setSrc] = useState(turn.sourceText);
  const [tgt, setTgt] = useState(turn.targetText ?? "");
  const [term, setTerm] = useState({ source: "", target: "" });
  const same = turn.targetText === turn.sourceText;
  return (
    <div id={`turn-${turn.seq}`} className={`turn ${turn.speaker === "interviewee" ? "ie" : ""} ${flash ? "flash" : ""}`}>
      <button className="when" onClick={onPlay} style={{ background: "none", border: 0, cursor: "pointer", textAlign: "left", padding: 0 }} title="Play from here">
        {fmtClock(turn.startedMs)}
      </button>
      <div>
        <div className="row" style={{ gap: 8 }}>
          <span className="who">{turn.speaker === "interviewer" ? "Interviewer" : turn.speakerLabel && turn.speakerLabel !== "Room" ? turn.speakerLabel : "Participant"}</span>
          {turn.origin === "transcribed" && <span className="xs muted">from the recording</span>}
          {turn.preparedQuestionId && <span className="faint xs">prepared question</span>}
          {turn.origin === "recovered" && <span className="faint xs">recovered after a dropped connection</span>}
          {turn.edited && <span className="faint xs">edited</span>}
          <span className="spacer" />
          {editable && !editing && (
            <button className="btn ghost small" onClick={() => setEditing(true)}>
              Correct
            </button>
          )}
        </div>
        {editing ? (
          <div className="stack tight" style={{ marginTop: 6 }}>
            <textarea className="input" value={src} onChange={(e) => setSrc(e.target.value)} aria-label="What was said" />
            {!same && <textarea className="input" value={tgt} onChange={(e) => setTgt(e.target.value)} aria-label="Translation" />}
            {!same && (
              <div className="row wrap small">
                <span className="muted">Add a glossary term (optional):</span>
                <input className="input" style={{ width: 160 }} placeholder={turn.sourceLang} value={term.source} onChange={(e) => setTerm({ ...term, source: e.target.value })} />
                <input className="input" style={{ width: 160 }} placeholder={turn.targetLang} value={term.target} onChange={(e) => setTerm({ ...term, target: e.target.value })} />
              </div>
            )}
            <div className="row">
              <button className="btn small primary" onClick={async () => {
                await api.patch(`${base}/turns/${turn.id}`, {
                  sourceText: src !== turn.sourceText ? src : undefined,
                  targetText: !same && tgt !== turn.targetText ? tgt : undefined,
                  addToGlossary: term.source && term.target ? term : undefined,
                });
                setEditing(false);
                onSaved();
              }}>
                Save correction
              </button>
              <button className="btn small ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="orig">{turn.sourceText}</div>
            {!same && turn.targetText && <div className="tran">{turn.targetText}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/** Before the interview: open it on this phone, and read the briefing. */
function Prepare({ detail, onChange }: { detail: Detail; onChange: () => void }) {
  const { tenant } = useParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const b = detail.briefing;
  return (
    <div className="grid2" style={{ marginBottom: 20 }}>
      <section className="panel">
        <h3>Ready to interview</h3>
        <p className="muted" style={{ margin: "6px 0 14px" }}>
          Open the interview on your phone. It shows a code for the participant's phone; nothing is recorded until they agree on their own screen.
        </p>
        <Link className="btn primary big" to={`/field/${tenant}/${detail.id}`}>
          {detail.status === "live" ? "Rejoin the interview" : "Open on this phone"}
        </Link>
      </section>
      <section className="panel">
        <div className="panel-head">
          <h3>Briefing</h3>
          <span className="spacer" />
          <button className="btn small" disabled={busy} onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.post(`${t(tenant!)}/interviews/${detail.id}/briefing`);
              onChange();
            } catch (err) {
              setError(err as Error);
            } finally {
              setBusy(false);
            }
          }}>
            {busy ? "Preparing…" : b ? "Refresh" : "Prepare briefing"}
          </button>
        </div>
        <ErrorNote error={error} />
        {b ? (
          <div className="stack tight small">
            <p style={{ fontWeight: 600 }}>{b.headline}</p>
            {([["Known so far", b.knownSoFar], ["Probe for", b.probeFor], ["Check against others", b.claimsToVerify], ["Questions worth asking", b.suggestedQuestions]] as const).map(
              ([label, list]) =>
                list.length > 0 && (
                  <div key={label}>
                    <b>{label}</b>
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                      {list.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ),
            )}
          </div>
        ) : (
          <p className="muted small">A one-page brief on this person's area: what other verified interviews said, what to probe and what to check.</p>
        )}
      </section>
    </div>
  );
}
